import Flickity from 'flickity'
import 'flickity/dist/flickity.min.css'

/**
 * CUSTOM ELEMENT
 * Flickity Element
 *
 * Using the Flickity instance under the hood this element allows us to add additional features such as
 * Controller buttons (top right)
 * Progress Bar
 *
 * For a working example of the x-flickity search for the 'article-grid.liquid' file.
 */
class FlickityElement extends HTMLElement {
  static NEXT_SELECTOR = '[data-role="next"]'
  static PREVIOUS_SELECTOR = '[data-role="previous"]'
  static PROGRESS_BAR_SELECTOR = '[data-role="progress-bar"]'
  static CONTROLLER_SELECTOR = '[data-role="controller"]'
  static CONTAINER_SELECTOR = '[data-role="container"]'

  constructor() {
    super()
    this.flickity = null

    this.initialiseFlickity = () => {
      if (!this.containerElement) return
      if (this.flickity) this.flickity.destroy()

      this.flickity = new Flickity(this.containerElement, {
        cellAlign: 'left',
        prevNextButtons: false,
        groupCells: 1,
        pageDots: false,
        imagesLoaded: true,
        on: {
          change: this.handleUpdateProgressBar.bind(this)
        }
      })
    }

    this.calculateProgress = () => {
      const selectedIndex = this.flickity.selectedIndex
      const groupCells = this.flickity.options.groupCells

      // Flickity thinks there's two more elements than there actually are
      const totalCells = Math.max(0, this.flickity.cells.length)
      const cellCount = Math.max(0, groupCells * Math.round(totalCells / groupCells) - 1)
      const visibleCells = Math.min(selectedIndex * groupCells, cellCount)

      const actualProgress = visibleCells / cellCount
      return 25 + 75 * actualProgress
    }
  }

  handleDisableButtons(progress) {
    if (!this.controllerElement) return

    if (progress <= 0) {
      this.previousElement?.setAttribute('disabled', '')
    } else if (progress >= 100) {
      this.nextElement?.setAttribute('disabled', '')
    } else {
      this.previousElement?.removeAttribute('disabled')
      this.nextElement?.removeAttribute('disabled')
    }
  }

  handleUpdateProgressBar() {
    if (!this.progressBarElement) return
    const progress = this.calculateProgress() || 25
    this.progressBarElement.style.setProperty('--progress', progress + '%')
    this.handleDisableButtons(progress)
  }

  handleUpdateCarousel(e) {
    const { role } = e.currentTarget.dataset
    switch (role) {
      case 'previous':
        this.flickity.previous()
        break
      case 'next':
        this.flickity.next()
        break
    }
  }

  fetchChildren() {
    this.containerElement = this.querySelector(FlickityElement.CONTAINER_SELECTOR)
    this.controllerElement = this.querySelector(FlickityElement.CONTROLLER_SELECTOR)
    this.progressBarElement = this.querySelector(FlickityElement.PROGRESS_BAR_SELECTOR)

    if (!this.controllerElement) return
    this.nextElement = this.controllerElement.querySelector(FlickityElement.NEXT_SELECTOR)
    this.previousElement = this.controllerElement.querySelector(FlickityElement.PREVIOUS_SELECTOR)
  }

  addEventListeners() {
    this.nextElement?.addEventListener('click', this.handleUpdateCarousel.bind(this))
    this.previousElement?.addEventListener('click', this.handleUpdateCarousel.bind(this))
  }

  removeEventListeners() {
    this.flickity.destroy()
    this.nextElement?.removeEventListener('click', this.handleUpdateCarousel.bind(this))
    this.nextPrevious?.removeEventListener('click', this.handleUpdateCarousel.bind(this))
  }

  connectedCallback() {
    this.fetchChildren()
    this.initialiseFlickity()
    this.addEventListeners()
    this.handleUpdateProgressBar()
  }

  disconnectedCallback() {
    this.removeEventListeners()
  }
}

customElements.define('x-flickity', FlickityElement)
